<template>
    <Row
        hoverable
        class="EntitiesTableRow"
        :class="{'EntitiesTableRow--alert': expiringSoon}"
    >
        <Cell
            v-if="rbacShowBulkActionCheckbox"
            class="EntitiesTableRow__selectionCell"
        >
            <Checkbox
                :model-value="selected"
                :disabled="isPerformingBulkAction"
                @update:model-value="selectionChange"
            />
        </Cell>

        <Cell
            class="EntitiesTableRow__nameCell"
            @click="selectEntity"
        >
            <VMenu
                :delay="{ show: 300, hide: 100 }"
                placement="right"
                popper-class="EntitiesTableRow__tooltip"
            >
                <div class="EntitiesTableRow__insuredNameEmailContainer tooltip-target">
                    <span class="EntitiesTableRow__nameText">{{ entity.displayName }}</span>
                    <span class="EntitiesTableRow__emailText">{{ entity.contactEmail }}</span>
                </div>
                <template #popper>
                    <EntityTooltipDetails :entity="entity" />
                </template>
            </VMenu>
        </Cell>
        <Cell
            class="EntitiesTableRow__entityEffectiveGroupCell"
            @click="selectEntity"
        >
            <EffectiveRiskProfilesTooltipLabel
                :risk-profile-names="entityGroupNames"
                :show-label-prefix="false"
                placement="right"
            />
        </Cell>
        <Cell
            v-for="customProperty in displayedCustomProperties"
            :key="customProperty.id"
            class="EntitiesTableRow__customProperty"
            @click="selectEntity"
        >
            {{ getValue(customProperty) }}
        </Cell>

        <Cell
            class="EntitiesTableRow__expirationCell"
            @click="selectEntity"
        >
            <div v-if="entity.nextExpiration">
                <div class="EntitiesTableRow__expirationDate">
                    {{ formattedDate }}
                </div>
                <template v-if="expired">
                    <div class="EntitiesTableRow__daysBeforeExpired">
                        Expired
                    </div>
                </template>
                <template v-else-if="daysTilExpired === 0">
                    <div class="EntitiesTableRow__daysBeforeExpired">
                        Expires today
                    </div>
                </template>
                <template v-else>
                    <div class="EntitiesTableRow__daysBeforeExpired">
                        {{ daysTilExpiredString }}
                    </div>
                </template>
            </div>
        </Cell>
        <Cell class="EntitiesTableRow__complianceStatusCell" @click="selectEntity">
            <ComplianceStatusBadge
                v-if="entity.active"
                class="EntitiesTableRow__badge"
                :status="entity.complianceStatus"
            />
        </Cell>
        <Cell class="EntitiesTableRow__verificationStatus" @click="selectEntity">
            <div v-if="entity.active">
                <VerificationStatusBadge
                    v-if="entity.paused"
                    class="EntitiesTableRow__badge"
                >
                    Paused
                </VerificationStatusBadge>
                <VerificationStatusBadge
                    v-else
                    class="EntitiesTableRow__badge"
                    :status="entity.verificationStatus"
                />
            </div>
            <div v-else>
                <Badge class="EntitiesTableRow__deactivatedBadge">
                    Deactivated
                </Badge>
            </div>
        </Cell>
        <Cell
            v-if="rbacShowActionButton"
            class="EntitiesTableRow__actionsCell"
        >
            <ActionsButton
                :entity="entity"
                :loading="isUpdating"
                :is-loading-submission-link="isLoadingSubmissionLink"
                :submission-link="submissionLink"
                hide-exception-options
                hide-view-mapping
                v-on="eventListeners"
            />
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { Menu as VMenu } from 'floating-vue';
    import moment from 'moment';

    import { Badge } from '@evidentid/dashboard-commons/components/Badge';
    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import {
        CustomProperty,
        Entity,
        EntityAddress,
    } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { toEventListeners } from '@evidentid/vue-commons/utils/attrsListeners';

    import ActionsButton from '@/modules/dashboard/components/ActionsButton/ActionsButton.vue';
    import EntityTooltipDetails from '@/modules/dashboard/components/EntityTooltipDetails/EntityTooltipDetails.vue';
    import VerificationStatusBadge
        from '@/modules/dashboard/components/VerificationStatusBadge/VerificationStatusBadge.vue';
    import EffectiveRiskProfilesTooltipLabel
        from '@/modules/entity-details/components/EffectiveRiskProfilesTooltipLabel/EffectiveRiskProfilesTooltipLabel.vue';
    import { getDisplayAddress } from '@/modules/entity-details/utils/getDisplayAddress';
    import {
        isCustomPropertyAddress,
    } from '@/modules/entity-management/utils/is-custom-property-address/isCustomPropertyAddress';

    import ComplianceStatusBadge from '../ComplianceStatusBadge/ComplianceStatusBadge.vue';

    @Component({
        emits: [ 'grantExceptionToEntity', 'loadEntityRequests', 'removeExceptions', 'selectEntity', 'selectionChange', 'upload-entity-document', 'view-submission-history' ],
        components: {
            RbacDisplayGuard, VerificationStatusBadge, Row, Cell, ComplianceStatusBadge,
            EntityTooltipDetails, ActionsButton, Badge, Checkbox, EffectiveRiskProfilesTooltipLabel,
            VMenu,
        },
    })
    export default class EntitiesTableRow extends Vue {
        @Prop({ type: Object })
        private entity!: Entity;

        @Prop({ type: Array, default: () => [] })
        private displayedCustomProperties!: CustomProperty[];

        @Prop({ type: Array, default: () => [] })
        private currentlyUpdatedEntitiesIds!: string[];

        @Prop({ type: Boolean, default: false })
        private selected!: boolean;

        @Prop({ type: Boolean, default: false })
        private isPerformingBulkAction!: boolean;

        @Prop({ type: Boolean, default: false })
        private isLoadingSubmissionLink!: boolean;

        @Prop({ type: Boolean, default: false })
        private rbacShowActionButton!: boolean;

        @Prop({ type: Boolean, default: false })
        private rbacShowBulkActionCheckbox!: boolean;

        @Prop({ type: String, default: '' })
        private submissionLink!: string;

        // PRODUCT-26110 Phase 2B2: MUST be a getter, not a class field. A field
        // initializer runs during construction, before vue-facing-decorator has wired up
        // the Options-API surface, so the arrow functions would capture a `this` whose
        // `$emit` is still undefined -> "this.$emit is not a function" when a listener
        // fires. A getter resolves `this` at access (render) time, when `$emit` exists.
        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): `$listeners` is gone in Vue 3 --
        // rebuild the bare-event-name shape this `v-on="eventListeners"` binding expects
        // from `$attrs`' `onXxx` keys.
        private get eventListeners(): Record<string, unknown> {
            return {
                ...toEventListeners(this.$attrs),
                'actionsButtonClicked': () => {
                    this.$emit('loadEntityRequests', this.entity.id);
                },
                'removeExceptions': (exceptionId: string) => {
                    this.$emit('removeExceptions', this.entity, [ exceptionId ]);
                },
                'grantExceptionToEntity': () => {
                    this.$emit('grantExceptionToEntity', this.entity);
                },
                'view-submission-history': () => {
                    this.$emit('view-submission-history', this.entity);
                },
                'upload-entity-document': () => {
                    this.$emit('upload-entity-document', this.entity);
                },
            };
        }

        private get daysTilExpired() {
            const expirationDate = moment(this.entity.nextExpiration).startOf('day');
            return expirationDate.diff(moment().startOf('day'), 'days');
        }

        private get expiringSoon(): boolean {
            return this.daysTilExpired >= 0 && this.daysTilExpired <= 30;
        }

        private get expired(): boolean {
            return this.daysTilExpired < 0;
        }

        private get daysTilExpiredString(): string {
            if (!this.entity.nextExpiration) {
                return '';
            }
            const days = this.daysTilExpired;
            return `${days} ${this.daysTilExpired > 1 ? 'days' : 'day'}`;
        }

        private get formattedDate(): string {
            return moment(this.entity.nextExpiration).format('MMM DD, YYYY');
        }

        private get isUpdating(): boolean {
            return this.currentlyUpdatedEntitiesIds.includes(this.entity.id);
        }

        private get entityGroupNames(): string[] {
            return (this.entity.effectiveGroup || [])
                .map((riskProfile) => riskProfile.displayName)
                .sort((a, b) => a.localeCompare(b));
        }

        private getValue(field: CustomProperty): string {
            const customPropertyValue = this.entity.insuredFields ? this.entity.insuredFields[field.key] : null;
            if (isCustomPropertyAddress(field)) {
                return getDisplayAddress({
                    schema: field.schema,
                    value: customPropertyValue as EntityAddress,
                });
            }
            switch (typeof customPropertyValue) {
                case 'boolean':
                    return customPropertyValue ? 'Yes' : 'No';
                case 'number':
                    // eventually we might consider take out en-us to support different countries when i18n needed
                    return customPropertyValue.toLocaleString(
                        'en-US',
                        { maximumFractionDigits: 0 },
                    );
                default:
                    return customPropertyValue?.toString() || '';
            }
        }

        private selectionChange(): void {
            this.$emit('selectionChange', this.entity);
        }

        private selectEntity(): void {
            this.$emit('selectEntity', this.entity);
        }

        private grantExceptionToEntity(): void {
            this.$emit('grantExceptionToEntity', this.entity);
        }
    }
</script>
